'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2 } from 'lucide-react';
import { useUIStore } from '@/stores/ui.store';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { InstitutionStyledSelect } from '@/components/institution/InstitutionStyledSelect';
import { TrainerRightPanelHeader } from '@/components/dashboard/TrainerRightPanelHeader';
import { v2TrainingContentRequestsApi } from '@/lib/v2-api';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

type InstitutionUserRow = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  roleLabel?: string;
};

/**
 * Panneau latéral global (portail) : saisie d’une demande de préparation de contenus formation.
 * Monté depuis `App.tsx` pour couvrir formateur (`AppLayout`) et apprenant.
 * Thème aligné sur les autres panneaux droits (Webhook, pipeline, etc.).
 */
export function TrainingRequestPanelPortal() {
  const { t } = useTranslation();
  const open = useUIStore((s) => s.trainingRequestPanelOpen);
  const setOpen = useUIStore((s) => s.setTrainingRequestPanelOpen);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [justification, setJustification] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [formateurUserId, setFormateurUserId] = useState('');
  const [teachers, setTeachers] = useState<InstitutionUserRow[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    submittingRef.current = submitting;
  }, [submitting]);

  useEffect(() => {
    setMounted(true);
  }, []);

  const reset = useCallback(() => {
    setTitle('');
    setDescription('');
    setJustification('');
    setPreferredDate('');
    setFormateurUserId('');
  }, []);

  const loadTeachers = useCallback(async () => {
    setTeachersLoading(true);
    try {
      const raw = await api.get<{ success?: boolean; data?: { users?: InstitutionUserRow[] } }>('/institution/users');
      const list = raw?.data?.users ?? [];
      const onlyTeachers = list.filter((u) => u.role === 'teacher');
      setTeachers(onlyTeachers);
    } catch {
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void loadTeachers();
  }, [open, loadTeachers]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submittingRef.current) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  const formateurOptions = useMemo(() => {
    const base: { value: string; label: string }[] = [
      {
        value: '',
        label: t('trainingRequest.formateurPlaceholder', 'Choisir un formateur…'),
      },
    ];
    return base.concat(
      teachers.map((u) => {
        const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
        return { value: u.id, label: `${name} (${u.email})` };
      }),
    );
  }, [teachers, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ttrim = title.trim();
    if (ttrim.length < 2) {
      toast.error(t('trainingRequest.titleRequired', 'Indiquez un titre (2 caractères minimum).'));
      return;
    }
    let providerHint: string | undefined;
    if (formateurUserId) {
      const u = teachers.find((x) => x.id === formateurUserId);
      if (u) {
        const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
        providerHint = `${name} — ${u.email}`;
      }
    }
    setSubmitting(true);
    try {
      await v2TrainingContentRequestsApi.create({
        title: ttrim,
        description: description.trim() || undefined,
        justification: justification.trim() || undefined,
        preferredDate: preferredDate.trim() || undefined,
        providerHint,
      });
      toast.success(
        t('trainingRequest.sent', 'Demande enregistrée. Vous serez informé·e par e-mail de la décision.'),
      );
      reset();
      setOpen(false);
      window.dispatchEvent(new CustomEvent('training-content-requests:refresh'));
    } catch {
      toast.error(
        t(
          'trainingRequest.sendError',
          'Impossible d’enregistrer la demande. Vérifiez votre connexion ou vos droits.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted || typeof document === 'undefined' || !open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex justify-end rtl:justify-start pointer-events-auto"
    >
      <div
        role="presentation"
        className="absolute inset-0 bg-black/20 backdrop-blur-[2px]"
        onClick={() => {
          if (!submittingRef.current) setOpen(false);
        }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="training-request-panel-title"
        className={cn(
          'relative z-10 h-full max-h-[100dvh] flex min-h-0 w-full flex-col overflow-hidden border-s border-[#E5DDD2] bg-white shadow-2xl dark:border-[#002952] dark:bg-[#001B3D] ltr:animate-slide-in-right rtl:animate-slide-in-left max-w-md',
        )}
      >
        <TrainerRightPanelHeader
          icon={FileText}
          title={t('trainingRequest.panelTitle', 'Demande de formation — contenus pédagogiques')}
          description={t(
            'trainingRequest.intro',
            'Décrivez le besoin. Les équipes RH valident ou non la demande ; en cas de validation, elle passe en préparation de contenu.',
          )}
          onClose={() => setOpen(false)}
          closeDisabled={submitting}
          titleId="training-request-panel-title"
          closeAriaLabel={t('common.close', 'Fermer')}
          shiftSupportChatFab={open}
          supportFabPanelMaxWidth="md"
        />

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain">
            <div className="min-h-0 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
              <Input
                id="tr-title"
                label={`${t('trainingRequest.fieldTitle', 'Titre de la formation')} *`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={500}
                required
                inputSize="sm"
              />

              <Textarea
                id="tr-desc"
                label={t('trainingRequest.fieldDescription', 'Description du besoin')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />

              <Textarea
                id="tr-just"
                label={t('trainingRequest.fieldJustification', 'Justification / objectifs')}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
              />

              <Input
                id="tr-date"
                label={t('trainingRequest.fieldDate', 'Date souhaitée')}
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                placeholder={t('trainingRequest.datePlaceholder', 'ex. T2 2026')}
                inputSize="sm"
              />

              <InstitutionStyledSelect
                id="tr-formateur"
                label={t('trainingRequest.fieldFormateur', 'Formateur de l’établissement')}
                hint={
                  teachersLoading
                    ? t('common.loading', 'Chargement…')
                    : teachers.length === 0
                      ? t('trainingRequest.formateursEmpty', 'Aucun compte formateur listé pour votre établissement.')
                      : undefined
                }
                options={formateurOptions}
                value={formateurUserId}
                onChange={setFormateurUserId}
                disabled={teachersLoading}
                panelZIndexClass="z-[160]"
              />
            </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50/90 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/80 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={submitting}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--inst-player-primary,#001B3D)] px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[color:var(--inst-player-primary-hover,#002952)] disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {submitting ? t('common.sending', 'Envoi…') : t('trainingRequest.submit', 'Envoyer la demande')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
